'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

interface Category {
  id: number;
  nameKey: string;
  icon: string;
  manufacturerCount: number;
  productCount: number;
}

const IndustryCategories = () => {
  const { t } = useTranslation();

  const categories: Category[] = [
    {
      id: 1,
      nameKey: 'yarnSpinning',
      icon: 'SparklesIcon',
      manufacturerCount: 450,
      productCount: 12500,
    },
    {
      id: 2,
      nameKey: 'fabricsTextiles',
      icon: 'SwatchIcon',
      manufacturerCount: 680,
      productCount: 28400,
    },
    {
      id: 3,
      nameKey: 'accessoriesTrims',
      icon: 'CubeIcon',
      manufacturerCount: 320,
      productCount: 15600,
    },
    {
      id: 4,
      nameKey: 'textileMachinery',
      icon: 'CogIcon',
      manufacturerCount: 150,
      productCount: 5800,
    },
  ];

  return (
    <section className="py-20 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            {t('browseByIndustry')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('categoriesDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              href="/industry-categories"
              className="bg-background border border-border rounded-lg p-6 hover:shadow-md hover:border-primary/50 transition-all duration-200 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors duration-200">
                  <Icon
                    name={category.icon as any}
                    size={24}
                    variant="outline"
                    className="text-primary"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-headline font-bold text-foreground mb-3 line-clamp-2">
                    {t(category.nameKey as any)}
                  </h3>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="BuildingOfficeIcon" size={14} variant="outline" />
                      <span>{category.manufacturerCount.toLocaleString()} {t('manufacturers')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="CubeIcon" size={14} variant="outline" />
                      <span>{category.productCount.toLocaleString()} {t('products')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            href="/industry-categories"
            className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-md font-headline font-semibold hover:bg-secondary/90 transition-colors duration-200"
          >
            {t('viewAllCategories')}
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default IndustryCategories;